import { motion } from 'framer-motion';
import { useData } from 'vike-react/useData';
import { getAudienceById } from '@shared/constants/audiences';
import { Container } from '../../components/layout/Container';
import { StaggerGrid, StaggerItem } from '../../components/motion/StaggerGrid';
import { CountUp } from '../../components/motion/CountUp';
import { BreadcrumbListLd } from '../../components/seo/jsonld/BreadcrumbList';
import { JourneyTimeline } from '../../components/blocks/JourneyTimeline';
import { ToolsForAudience } from '../../components/blocks/ToolsForAudience';
import { ConcreteCase } from '../../components/blocks/ConcreteCase';
import { SectorHero } from '../../components/sector/SectorHero';
import { SectorProof } from '../../components/sector/SectorProof';
import { SectorFAQ } from '../../components/sector/SectorFAQ';
import { SectorCTA } from '../../components/sector/SectorCTA';
import { getAudienceVoice } from '../../config/audience-voice';
import { audienceLegacyTone } from '../../config/audience-theme';
import { easeStripe } from '../../lib/motion';
import type { Data } from './+data';

const AUDIENCE_ID = 'professionnels' as const;

const KPI = [
  {
    value: 'jusqu’à −18 %',
    label: 'd’économie sur la facture énergie, selon votre profil',
    source: 'Exemple de mise en concurrence',
  },
  {
    value: '0 €',
    label: 'frais de diagnostic',
    source: 'Diagnostic gratuit, sans engagement',
  },
  {
    value: '≈ 14 j',
    label: 'délai moyen de mise en concurrence',
    source: 'Délai constaté en moyenne',
  },
];

export default function ProfessionnelsPage() {
  const { journey, tools, cases } = useData<Data>();
  const audience = getAudienceById(AUDIENCE_ID)!;
  const voice = getAudienceVoice(AUDIENCE_ID);
  const legacyTone = audienceLegacyTone(AUDIENCE_ID);

  return (
    <>
      <BreadcrumbListLd
        items={[
          { name: 'Accueil', url: 'https://solutionecofrance.fr/' },
          { name: 'Professionnels', url: 'https://solutionecofrance.fr/professionnels' },
        ]}
      />

      <SectorHero
        audience={audience}
        voice={voice}
        sideSlot={<ProfessionnelsDashboardPreview />}
      />

      {/* KPI bar — preuves chiffrées clés */}
      <section className="border-y border-stone-200/60 bg-stone-50/50 py-10">
        <Container>
          <StaggerGrid className="grid gap-px overflow-hidden rounded-xl bg-stone-200/60 sm:grid-cols-3">
            {KPI.map((k) => (
              <StaggerItem key={k.label} className="bg-paper p-5">
                <p className="metric-xl">{k.value}</p>
                <p className="mt-1.5 text-xs text-stone-700">{k.label}</p>
                <p className="mt-0.5 text-[10px] text-stone-400">Source : {k.source}</p>
              </StaggerItem>
            ))}
          </StaggerGrid>
        </Container>
      </section>

      <SectorProof audience={audience} voice={voice} variant="bento" />

      {journey && (
        <JourneyTimeline
          eyebrow={voice.method.eyebrow}
          title={voice.method.title}
          description={voice.method.description}
          totalDuration={journey.totalDuration}
          ctaLabel={journey.ctaLabel}
          ctaHref={journey.ctaHref}
          steps={journey.steps}
          tone={legacyTone}
        />
      )}

      {tools.length > 0 && (
        <ToolsForAudience
          eyebrow={voice.tools.eyebrow}
          title={voice.tools.title}
          description={voice.tools.description}
          tools={tools.map((t) => ({
            title: t.title,
            description: t.description,
            iconName: t.iconName,
            href: t.href,
            badge: t.badge,
            metric: t.metric,
            metricLabel: t.metricLabel,
          }))}
          tone={legacyTone}
        />
      )}

      {cases.length > 0 && (
        <ConcreteCase
          eyebrow={voice.caseStudy.eyebrow}
          title={voice.caseStudy.title}
          cases={cases}
          tone={legacyTone}
        />
      )}

      <SectorFAQ audience={audience} voice={voice} />

      <SectorCTA audience={audience} voice={voice} />
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// ProfessionnelsDashboardPreview — side card hero
// Aperçu d'un audit factures B2B : sur-puissance détectée, comparatif
// marché, primes CEE BAT-* mobilisables. Pattern aligné avec
// AgriculteurPreview / ArtisanDashboardPreview.
// ════════════════════════════════════════════════════════════════

function ProfessionnelsDashboardPreview() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.97, y: 16 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 0.18, ease: easeStripe }}
      className="rounded-2xl border border-stone-200/70 bg-paper p-5 shadow-[0_1px_2px_rgba(10,10,10,.04)] ring-1 ring-inset ring-brand-200/60 md:p-6"
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-stone-500">
            Audit factures · Acme SARL
          </p>
          <p className="mt-0.5 text-xs text-stone-500">Commerce · 36 kVA (puissance souscrite) · option tarifaire jaune</p>
        </div>
        <span className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-[10px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-200/50">
          <span className="h-1 w-1 rounded-full bg-brand-500 animate-glow-pulse" />
          −18 % détecté
        </span>
      </div>

      <div className="mt-5 flex items-baseline gap-2 font-display tracking-tight">
        <span className="text-3xl font-bold text-ink md:text-4xl">
          <CountUp end={4280} suffix=" €" />
        </span>
        <span className="text-base text-stone-500">/ an estimé</span>
      </div>

      <div className="mt-5 space-y-1.5">
        {[
          {
            label: "TURPE (tarif d'acheminement réseau) · sur-puissance (8 kVA en trop)",
            val: '−1 240 €',
            status: 'Optimisable',
            tone: 'brand',
          },
          {
            label: 'Comparatif marché (5 fournisseurs)',
            val: '−2 100 €',
            status: 'Bascule',
            tone: 'accent',
          },
          {
            label: 'Prime CEE — pompe à chaleur tertiaire (fiche BAT-TH-104)',
            val: '+ 940 €',
            status: 'Éligible',
            tone: 'lavender',
          },
        ].map((row) => (
          <div
            key={row.label}
            className="flex items-center justify-between rounded-lg border border-stone-200/70 bg-stone-50/40 px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs text-stone-700">{row.label}</p>
              <p
                className={`mt-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                  row.tone === 'brand'
                    ? 'text-brand-700'
                    : row.tone === 'lavender'
                      ? 'text-lavender-700'
                      : 'text-accent-700'
                }`}
              >
                {row.status}
              </p>
            </div>
            <span className="ml-2 font-mono text-xs font-semibold text-ink tabular-nums">
              {row.val}
            </span>
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-between border-t border-stone-200/60 pt-3">
        <p className="text-[11px] text-stone-500">Mise en concurrence</p>
        <p className="font-mono text-[12px] font-semibold text-brand-700 tabular-nums">
          J+14
        </p>
      </div>
    </motion.div>
  );
}
